"use client"
import React, { useRef, useEffect, useState } from "react"
import { useTransitionRouter } from "next-view-transitions"
import { useRouter } from "next/navigation"
/* import { useGSAP } from "@gsap/react" */
import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"
import sanitizeHtml from 'sanitize-html'
import ReactLenis from "lenis/react"
import { slideInOut } from "@/app/lib/gsapAnimation"
import ImagePlaceholder from "../ui/ImagePlaceHolder"
import TextSplit from "../ui/TextSplit"
import Testimonial from "../ui/Testimonial"
import { IconQuote } from "@tabler/icons-react"
import { InteractiveHoverButton } from "../magicui/interactive-hover-button"

const viewTransitionStyles = `
  ::view-transition-old(work-image),
  ::view-transition-new(work-image) {
    animation-duration: 0.6s;
    mix-blend-mode: normal;
  }
  ::view-transition-old(work-image) {
    animation-name: fade-out;
  }
  ::view-transition-new(work-image) {
    animation-name: fade-in;
  }
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes fade-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  [data-transitioning="true"] img {
    transition: opacity 0.3s ease;
  }
`

gsap.registerPlugin(ScrollTrigger)

export default function Details({ data, previous, type = "insights" }) {
  const [isloading, setIsloading] = useState(false)
  const { slug, acf, title, excerpt, featuredImageSrc, featuredImageAlt } = data

  const router = useRouter()
  const transitionRouter = useTransitionRouter()
  const src = featuredImageSrc
  const alt = featuredImageAlt
  const { title: previousTitle, _embedded: previousEmbedded } = previous
  const previousSrc = previousEmbedded["wp:featuredmedia"]?.[0]?.media_details?.sizes?.large?.source_url || previousEmbedded["wp:featuredmedia"][0]?.source_url || ""
  const previousAlt = previousEmbedded["wp:featuredmedia"]?.[0]?.alt_text
  const containerRef = useRef(null)
  const progressRef = useRef(null)

  const transitionName = `work-${slug}`

  useEffect(() => {
    const styleElement = document.createElement("style")
    styleElement.textContent = viewTransitionStyles
    styleElement.id = "view-transition-styles"


    if (!document.getElementById("view-transition-styles")) {
      document.head.appendChild(styleElement)
    }
    return () => {
      const existingStyle = document.getElementById("view-transition-styles")
      if (existingStyle) {
        existingStyle.remove()
      }
    }
  }, [])

  const handleNavigation = targetSlug => {
    if (!document.startViewTransition) {
      router.push(`/work/${targetSlug}`)
      return
    }
    document.startViewTransition(() => {
      setTimeout(()=> {
        transitionRouter.push(`/work/${targetSlug}`, {
          onTransitionReady: slideInOut
        })
      }, 1150)
    })
  }

  useEffect(() => {
    const container = containerRef.current
    const progressBar = progressRef.current
    if (!container || !progressBar || !previous) return

    const sections = Array.from(container.querySelectorAll(".panel"))
    if (!sections.length) return

    const extraScrollDistance = 1500
    const hasNavigated = { current: false }

    const getScrollValues = () => {
      const panelWidths = sections.reduce((total, panel) => total + panel.offsetWidth, 0)
      return {
        horizontalScrollEnd: panelWidths,
        totalScrollEnd: panelWidths + extraScrollDistance,
        extraStart: panelWidths,
        extraEnd: panelWidths + extraScrollDistance
      }
    }

    const timeline = gsap.to(sections, {
      xPercent: -100 * (sections.length - 1),
      ease: "none",
      scrollTrigger: {
        trigger: container,
        pin: true,
        scrub: 1,
        end: () => `+=${getScrollValues().totalScrollEnd}`,
        onUpdate: self => {
          const { extraStart, extraEnd, horizontalScrollEnd } = getScrollValues()
          const scrollPos = self.scroll()

          if (scrollPos >= extraStart && scrollPos + 1500 > horizontalScrollEnd) {
            const progress = (scrollPos - extraStart) / extraScrollDistance
            progressBar.style.opacity = "1"
            progressBar.style.width = `${Math.min(progress * 100, 100) + 4}%`
          } else {
            progressBar.style.opacity = "0"
            progressBar.style.width = "0%"
          }

          if (!hasNavigated.current && scrollPos >= extraEnd - 30) {
            hasNavigated.current = true
            setIsloading(true)
            setTimeout(() => handleNavigation(previous?.slug), 400)
          }
        }
      }
    })

    const handleResize = ScrollTrigger.refresh

    // Safeguard ResizeObserver
    let resizeObserver
    try {
      resizeObserver = new ResizeObserver(handleResize)
      resizeObserver.observe(container)
    } catch (error) {
      console.error("ResizeObserver failed:", error)
    }

    return () => {
      timeline.kill()
      ScrollTrigger.getAll().forEach(st => st.kill())
      if (resizeObserver) resizeObserver.disconnect()
      hasNavigated.current = false
    }
  }, [previous])
  

  useEffect(() => {
    const timeout = setTimeout(() => ScrollTrigger.refresh(), 250)
    return () => clearTimeout(timeout)
  }, [acf?.work_gallery])

  return (
    <ReactLenis root>
      <div className="horizontal-wrapper">
        <div className="w-full fixed h-full blur-3xl"></div>
        <div className="panels-container" ref={containerRef}>
          {isloading && <div className="fixed bg-white w-screen h-screen z-[9999] opacity-50 transition-opacity duration-500 ease-in-out delay-75"></div>}

          {/* Presentation */}
          <section className="panel panel-1 flex items-center gap-20 lg:grid lg:grid-cols-12">
            <div className="flex flex-col text-black p-16 col-span-5">
              <div className="flex gap-x-10 items-center">
                <a
                  onClick={e => {
                    e.preventDefault()
                    setTimeout(() => {
                      transitionRouter.push(`/${type}`, { onTransitionReady: slideInOut })
                    }, 300)
                    
                  }}
                >
                  <InteractiveHoverButton>List of work ←</InteractiveHoverButton>
                </a>
              </div>
              <h1 className="text-[4vw] xl:text-[4.5vw] 2xl:text-[5vw] pt-12" dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }} />

              <TextSplit>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(excerpt, { ALLOWED_TAGS: [] }) }} />
              </TextSplit>
            </div>
            <div className="relative col-span-7">
              <figure className="relative h-[70vh] aspect-video w-auto overflow-hidden max-w-full min-w-1/2 rounded-3xl flex items-center justify-center p-6">
                {/** First image */}
                {src && (
                  
                    <img src={src} alt={alt} className="object-cover rounded-3xl h-full size-max" style={{viewTransitionName: transitionName}}/>
                
                )}
              </figure>
            </div>
          </section>

          {/* Challenge */}
          <section className="panel linear-gradient-dark-light-green text-white xl:px-16">
            <div className="flex gap-x-8 2xl:gap-x-12 items-center justify-center">
              <div className="w-1/3  max-w-[40vw] text-lg flex flex-col">
                <h3 className="font-semibold pb-8">What we did</h3>
                <div dangerouslySetInnerHTML={{ __html: acf["work_provided_services"] }} className="text-base" />
              </div>
              {acf?.work_challenge_video ? (
                <video width="1280" height="960" autoPlay loop={false} muted preload="auto" className="max-w-[75vw]">
                  <source src={acf?.work_challenge_video?.url} type={acf?.work_challenge_video?.mime_type} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <ImagePlaceholder imageUrl={acf?.work_challenge_illustration?.url} alt={acf?.work_challenge_illustration?.alt} isVertical={false} />
              )}
            </div>
          </section>

          {/* Solution */}
          {acf?.work_solution && (
            <section className="panel linear-yellow-orange text-white xl:px-20 2xl:px-28">
              <div className="grid grid-cols-3 gap-x-20">
                {/* Challenges */}
                <div className="flex flex-col">
                  <h3 className="font-semibold pb-8">Challenges</h3>
                  <div dangerouslySetInnerHTML={{ __html: acf["work_challenge"] }} className="text-base" />
                </div>

                {/* Solution */}

                <div className="col-span-2 flex flex-col">
                  <h3 className="font-semibold pb-8">Solution</h3>

                  <div className="grid grid-cols-[repeat(auto-fit,_minmax(300px,_1fr))] gap-x-16 text-base" dangerouslySetInnerHTML={{ __html: sanitizeHtml(acf?.work_solution) }} />
                </div>
              </div>
            </section>
          )}

          {/* Gallery */}
          {acf?.work_gallery?.map((panel, i) => {
            return (
              <section className="panel bg-white xl:px-16" key={i}>
                <div className="flex gap-20 items-center w-full">
                  <div className="w-fit text-lg flex flex-col">
                    <div className="text-lg" dangerouslySetInnerHTML={{ __html: panel["work_gallery_media_legend"] }} />
                  </div>

                  <div className="relative h-[70vh] aspect-video overflow-hidden max-w-full min-w-1/2 rounded-3xl flex items-center justify-center w-auto">
                    <ImagePlaceholder imageUrl={panel?.work_gallery_media?.url} alt={panel?.work_gallery_media?.alt} isVertical={true} />
                  </div>
                </div>
              </section>
            )
          })}

          {/* Testimonials */}
          {acf?.work_testimonials?.[0] && (
            <section className="panel bg-[#FFF9F5] xl:px-16">
              <div className="flex gap-20 items-center">
                <div className="flex flex-col max-w-screen-sm items-center justify-center">
                  <div>
                    <IconQuote className="size-10" />
                  </div>
                  <h3 className="font-semibold">Client testimonial</h3>
                  <Testimonial id={acf?.work_testimonials[0]} />
                </div>
              </div>
            </section>
          )}

          <section className="panel bg-[#003831]">
            <div className="flex p-16 w-screen flex-col">
              <span className="text-white">Scroll to next {type === "work" ? "project" : type} →</span>
              <h1 className="text-[5vw] text-[#FF3C5C]" dangerouslySetInnerHTML={{ __html: sanitizeHtml(previousTitle?.rendered) }} />
              <div className="h-2 bg-black/30 w-full max-w-72 z-50">
                <div ref={progressRef} className="h-full bg-white transition-all duration-200 ease-out" style={{ width: "0%", opacity: 0 }} />
              </div>
            </div>
            {previousSrc && (
              <div className="relative">
                <figure className="h-screen overflow-hidden -w-1/2">{previousSrc && <img src={previousSrc} alt={previousAlt} className="object-cover h-full" />}</figure>
              </div>
            )}
          </section>
        </div>
      </div>
    </ReactLenis>
  )
}
